import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaTrashAlt, FaPlus } from "react-icons/fa";
import axiosInstance from "../../../apis/axiosConfig";
import { useDarkMode } from "../../../hooks/useDarkMode";

export default function LinkedDomain() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState("");

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/projects/list/");
      if (res.data?.status === "success") {
        const projects = res.data.projects || [];
        // Map to local shape { id, url }
        setDomains(projects.map((p) => ({ id: p.project_id, url: p.url })));
      } else {
        setDomains([]);
      }
    } catch (err) {
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const removeDomain = async (id) => {
    try {
      await axiosInstance.delete(`/projects/delete/${id}/`);
      setDomains((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {}
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      const payload = { projectName: newDomain, url: newDomain };
      const res = await axiosInstance.post("/projects/", payload);
      if (res.status === 201 || (res.data && res.data.status === "success")) {
        // refresh list
        fetchDomains();
        setNewDomain("");
        setShowAddModal(false);
      }
    } catch (err) {}
  };

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <div
      className={`p-3 sm:p-4 md:p-6 w-full sm:w-[95%] md:w-4/5 mx-auto rounded-lg sm:rounded-2xl shadow my-6 sm:my-10 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}
    >
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-lg transition-colors duration-200 shadow-sm ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-white"}`}
        >
          <FaArrowLeft
            className={`text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
        <h1
          className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
        >
          Linked Domains
        </h1>
      </div>

      <p className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
        You have{" "}
        <strong className={isDarkMode ? "text-gray-200" : "text-gray-800"}>
          {domains.length}
        </strong>{" "}
        linked domain{domains.length !== 1 ? "s" : ""}
      </p>

      <button
        onClick={() => setShowAddModal(true)}
        className="mb-6 inline-flex items-center gap-2 bg-[#008300] text-white px-4 py-2 rounded-md hover:bg-[#006600] transition"
      >
        <FaPlus /> Add Domain
      </button>

      <div className="space-y-3 relative z-0">
        {loading ? (
          <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Loading...
          </div>
        ) : domains.length === 0 ? (
          <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            No linked domains found.
          </div>
        ) : (
          domains.map((d) => (
            <div
              key={d.id}
              className={`rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:shadow-md transition relative z-0 ${isDarkMode ? "bg-gray-700" : "bg-white"}`}
            >
              <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${isDarkMode ? "bg-[#008300]" : "bg-green-200"}`}
                >
                  <span
                    className={`font-semibold text-lg ${isDarkMode ? "text-[#006600]" : "text-green-600"}`}
                  >
                    🔗
                  </span>
                </div>
                <div className="min-w-0">
                  <p
                    className={`font-medium break-words sm:truncate sm:max-w-[360px] ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                    title={d.url}
                  >
                    {d.url}
                  </p>
                </div>
              </div>

              <button
                onClick={() => removeDomain(d.id)}
                type="button"
                className={`inline-flex items-center justify-center gap-1 px-4 py-2 rounded-full text-sm font-medium text-red-600 transition w-full sm:w-auto flex-shrink-0 relative z-10 border ${isDarkMode ? "border-gray-600 hover:bg-red-900/30 hover:border-red-500" : "border-gray-300 hover:bg-red-50 hover:border-red-400"}`}
              >
                <FaTrashAlt size={14} /> Remove
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            className={`rounded-lg shadow-lg w-full max-w-md p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}
            >
              Add New Domain
            </h3>

            <div className="mb-4">
              <label
                className={`block text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                Domain URL
              </label>
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="https://example.com"
                className={`w-full outline shadow focus:outline-2 px-3 py-2 rounded ${isDarkMode ? "bg-gray-700 text-gray-200 outline-gray-600 focus:outline-[#008300] placeholder-gray-500" : "bg-white text-gray-800 outline-gray-300 focus:outline-[#008300]"}`}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewDomain("");
                }}
                className={`px-4 py-2 rounded border transition ${isDarkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"}`}
              >
                Cancel
              </button>
              <button
                onClick={addDomain}
                className="px-4 py-2 bg-[#008300] text-white rounded hover:bg-[#006600]"
              >
                Add Domain
              </button>
            </div>
          </div>
        </div>
      )}
    </div></>
  );
}
